import type { IStudentBadge } from '@/entities/Badges';
import badgeEarnedArt from '@/shared/assets/images/badge-earned.svg?url';

const SIZE = 1080;
const PADDING = 64;
const ART = 520;

/**
 * Инстаграм не умеет публиковать по ссылке — пост собирается из галереи. Поэтому для
 * него единственный путь: отдать картинку файлом, а человек выложит её сам.
 */
const loadImage = (src: string, withCors: boolean): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    if (withCors) image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Не загрузилось: ${src}`));
    image.src = src;
  });

/**
 * Иконку с чужого домена нужно просить с CORS, иначе холст «пачкается» и `toBlob`
 * бросает SecurityError. Если сервер заголовков не отдал — рисуем макетный арт.
 */
const loadArt = async (badge: IStudentBadge): Promise<HTMLImageElement> => {
  if (badge.iconUrl) {
    try {
      return await loadImage(badge.iconUrl, true);
    } catch {
      // иконка не отдалась — ниже подставим макетную
    }
  }

  return loadImage(badgeEarnedArt, false);
};

const roundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
};

/** Длинное название переносим по словам, чтобы не уехало за края. */
const wrapTitle = (measure: (text: string) => number, title: string, maxWidth: number): string[] => {
  const lines: string[] = [];
  let line = '';

  title.split(' ').forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (measure(candidate) > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);

  return lines.slice(0, 2);
};

export const downloadBadgeImage = async (badge: IStudentBadge): Promise<void> => {
  const art = await loadArt(badge);

  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Та же подложка, что под бейджем в кабинете
  const panel = SIZE - PADDING * 2;
  const gradient = ctx.createLinearGradient(PADDING, PADDING, SIZE - PADDING, SIZE - PADDING);
  gradient.addColorStop(0, 'rgba(33, 29, 85, 0.05)');
  gradient.addColorStop(1, 'rgba(99, 0, 0, 0.05)');
  ctx.fillStyle = gradient;
  roundedRect(ctx, PADDING, PADDING, panel, panel, 48);
  ctx.fill();

  const artTop = PADDING + 150;
  ctx.drawImage(art, (SIZE - ART) / 2, artTop, ART, ART);

  ctx.fillStyle = '#1a141f';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = 'bold 64px Arial, sans-serif';

  const titleTop = artTop + ART + 80;
  const measure = (text: string) => ctx.measureText(text).width;
  wrapTitle(measure, badge.title, SIZE - PADDING * 4).forEach((line, index) => {
    ctx.fillText(line, SIZE / 2, titleTop + index * 76);
  });

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Could not build the image');

  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = `${badge.code || 'badge'}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};
